import React from "react";
import FloatingCard from "../../ui/FloatingCard";
import FloatingButton from "../../ui/FloatingButton";
import { getSportEmoji } from "../../../utils/sportIcons";

/**
 * Team Requests Tab Component
 * Displays pending join requests with approve/reject actions,
 * and tournament/league team invitations for team owners.
 */
const TeamRequestsTab = ({
  joinRequests,
  onApproveRequest,
  onRejectRequest,
  tournamentInvitations,
  onAcceptInvitation,
  onDeclineInvitation,
}) => {
  const hasRequests     = joinRequests && joinRequests.length > 0;
  const hasInvitations  = tournamentInvitations && tournamentInvitations.length > 0;

  if (!hasRequests && !hasInvitations) {
    return (
      <div className="requests-content-gaming">
        <FloatingCard className="empty-state-gaming delay-4">
          <div className="empty-content-gaming">
            <div className="empty-icon-gaming">📝</div>
            <h3 className="empty-title-gaming">No Pending Requests</h3>
            <p className="empty-text-gaming">
              There are no pending join requests or tournament invitations at the moment.
            </p>
          </div>
        </FloatingCard>
      </div>
    );
  }

  return (
    <div className="requests-content-gaming" style={{ maxHeight: "70vh", overflowY: "auto", paddingRight: "0.25rem" }}>

      {/* ── Join Requests Section ── */}
      {hasRequests && (
        <>
          <h4 style={{ color: "#a78bfa", marginBottom: "0.75rem", marginTop: 0, fontSize: "1rem" }}>
            👥 Join Requests ({joinRequests.length})
          </h4>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {joinRequests.map((request, index) => {
              const requestDate = request.requested_at || request.created_at;
              const dateStr = requestDate ? new Date(requestDate).toLocaleDateString() : null;
              return (
                <FloatingCard
                  key={request.user_id}
                  className={`request-card-gaming delay-${(index % 6) + 4}`}
                  variant="outline"
                  size="medium"
                  style={{ padding: 0 }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "1rem", padding: "1rem 1.25rem" }}>
                    {/* Avatar */}
                    <div style={{ flexShrink: 0 }}>
                      {(request.avatar_url || request.profile_picture) ? (
                        <img
                          src={request.avatar_url || request.profile_picture}
                          alt={request.username}
                          style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover", border: "2px solid rgba(99,102,241,0.4)" }}
                        />
                      ) : (
                        <div style={{ width: 40, height: 40, borderRadius: "50%", background: "rgba(99,102,241,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.2rem", border: "2px solid rgba(99,102,241,0.3)" }}>
                          👤
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, color: "#e2e8f0", fontSize: "0.95rem", marginBottom: "0.2rem" }}>
                        {request.first_name} {request.last_name}
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem 0.9rem", fontSize: "0.78rem", color: "rgba(255,255,255,0.55)" }}>
                        <span>@{request.username}</span>
                        {dateStr && <span>📅 {dateStr}</span>}
                      </div>
                      {request.message && (
                        <p style={{ margin: "0.3rem 0 0", fontSize: "0.8rem", color: "rgba(255,255,255,0.55)", fontStyle: "italic" }}>
                          "{request.message}"
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", flexShrink: 0 }}>
                      <FloatingButton
                        variant="success"
                        size="small"
                        onClick={() => onApproveRequest(request.user_id, request.username)}
                        icon="✅"
                      >
                        <span className="btn-text">Approve</span>
                      </FloatingButton>
                      <FloatingButton
                        variant="danger"
                        size="small"
                        onClick={() => onRejectRequest(request.user_id, request.username)}
                        icon="❌"
                      >
                        <span className="btn-text">Reject</span>
                      </FloatingButton>
                    </div>
                  </div>
                </FloatingCard>
              );
            })}
          </div>
        </>
      )}

      {/* ── Tournament / League Invitations Section ── */}
      {hasInvitations && (
        <div style={{ marginTop: hasRequests ? "2rem" : 0 }}>
          <h4 style={{ color: "#a78bfa", marginBottom: "0.75rem", marginTop: 0, fontSize: "1rem" }}>
            🏆 Tournament &amp; League Invitations ({tournamentInvitations.length})
          </h4>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {tournamentInvitations.map((inv, index) => {
              const contextType  = inv.tournament_id ? "Tournament" : "League";
              const contextName  = inv.tournament_name || inv.league_name || `${contextType} #${inv.tournament_id || inv.league_id}`;
              const contextSport = inv.tournament_sport || inv.league_sport;
              const invitedDate  = new Date(inv.created_at).toLocaleDateString();
              return (
                <FloatingCard
                  key={inv.id}
                  className={`request-card-gaming delay-${(index % 6) + 4}`}
                  variant="outline"
                  size="medium"
                  style={{ padding: 0 }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "1rem", padding: "1rem 1.25rem" }}>
                    {/* Icon */}
                    <div style={{ fontSize: "1.8rem", flexShrink: 0, lineHeight: 1 }}>
                      {contextType === "Tournament" ? "🏆" : "🏅"}
                    </div>

                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, color: "#e2e8f0", fontSize: "0.95rem", marginBottom: "0.25rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {contextName}
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem 0.9rem", fontSize: "0.78rem", color: "rgba(255,255,255,0.55)" }}>
                        {contextSport && <span>{getSportEmoji(contextSport)} {contextSport}</span>}
                        <span>📅 {invitedDate}</span>
                        <span>👤 By {inv.invited_by_name || inv.invited_by_username || "Organizer"}</span>
                      </div>
                      {inv.message && (
                        <p style={{ margin: "0.35rem 0 0", fontSize: "0.8rem", color: "rgba(255,255,255,0.55)", fontStyle: "italic" }}>
                          "{inv.message}"
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", flexShrink: 0 }}>
                      <FloatingButton
                        variant="success"
                        size="small"
                        onClick={() => onAcceptInvitation(inv.id)}
                        icon="✅"
                      >
                        <span className="btn-text">Accept</span>
                      </FloatingButton>
                      <FloatingButton
                        variant="danger"
                        size="small"
                        onClick={() => onDeclineInvitation(inv.id)}
                        icon="❌"
                      >
                        <span className="btn-text">Decline</span>
                      </FloatingButton>
                    </div>
                  </div>
                </FloatingCard>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamRequestsTab;
